import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'pa_user_token';
const SESSION_KEY = 'pa_session_id';

const AuthContext = createContext(null);

const ensureSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalState, setAuthModalState] = useState({ open: false, mode: 'login', onSuccess: null, intent: null });
  const sessionId = ensureSessionId();

  // Bootstrap: if token exists, fetch /me
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUser(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); })
      .finally(() => setLoading(false));
  }, []);

  const setSession = useCallback((token, userData) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(userData);
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password }, {
      headers: { 'X-Session-Id': sessionId }
    });
    setSession(r.data.access_token, r.data.user);
    return r.data.user;
  }, [setSession, sessionId]);

  const register = useCallback(async ({ email, password, name, phone }) => {
    const r = await axios.post(`${API}/auth/register`, { email, password, name, phone }, {
      headers: { 'X-Session-Id': sessionId }
    });
    setSession(r.data.access_token, r.data.user);
    return r.data.user;
  }, [setSession, sessionId]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (updates) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const r = await axios.put(`${API}/auth/me`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(r.data);
    return r.data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const r = await axios.post(`${API}/auth/forgot-password`, { email });
    return r.data;
  }, []);

  const openAuthModal = useCallback((opts = {}) => {
    setAuthModalState({
      open: true,
      mode: opts.mode || 'login',
      onSuccess: opts.onSuccess || null,
      intent: opts.intent || null,
    });
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModalState({ open: false, mode: 'login', onSuccess: null, intent: null });
  }, []);

  // Authed axios helper (used by feature hooks)
  const authedFetch = useCallback((method, path, data) => {
    const token = localStorage.getItem(TOKEN_KEY);
    return axios({ method, url: `${API}${path}`, data,
      headers: token ? { Authorization: `Bearer ${token}` } : {} });
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, sessionId,
      login, register, logout, updateProfile, forgotPassword,
      authModalState, openAuthModal, closeAuthModal,
      authedFetch,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
